/**
 * Evaluation Search API — Tier 1 + Tier 2
 *
 * Typed wrapper around GET /api/evaluations/search.
 * Tier 1: template/athlete/channel/date filters, sort, pagination.
 * Tier 2: score range, video-status filter, body-part multi-select.
 *   Tier-2 params are passed through unconditionally; the backend gracefully
 *   ignores unknown params if Tier-2 indexes are not yet live.
 */

import { apiClient } from './api';

// ─── Query params ─────────────────────────────────────────────────────────────

export interface EvaluationSearchParams {
  template_id?: string;
  /** Coach/admin only. Passing a different user's ID as an athlete returns 403. */
  athlete_id?: string;
  channel_id?: string;
  /** ISO 8601 datetime — inclusive lower bound on created_at. */
  date_from?: string;
  /** ISO 8601 datetime — inclusive upper bound on created_at. */
  date_to?: string;
  sort_by?: 'created_at' | 'overall_score' | 'form_score' | 'timing_score';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  // ── Tier 2 (added in B-2025-0014) ──────────────────────────────────────────
  /** Inclusive lower bound on overall_score (0–100). */
  score_min?: number;
  /** Inclusive upper bound on overall_score (0–100). */
  score_max?: number;
  /**
   * Filter by video file availability.
   * - "any"       → no filter (default)
   * - "available" → only evals where the raw video still exists
   * - "expired"   → only evals whose video has been purged
   */
  video_status?: 'any' | 'available' | 'expired';
  /**
   * Repeatable body-part label filter (matches observation_types array).
   * Pass multiple values as an array; the backend treats this as OR.
   * Example: body_part=["left_knee", "right_hip"]
   */
  body_part?: string[];
}

// ─── Response types ───────────────────────────────────────────────────────────

export interface EvaluationSearchItem {
  /** Primary key — always 'evaluation_id', never 'id'. */
  evaluation_id: string;
  template_id: string | null;
  template_name: string | null;
  overall_score: number | null;
  form_score: number | null;
  timing_score: number | null;
  body_form_score: number | null;
  created_at: string;
  athlete_video_id: string | null;
  channel_id: string | null;
  video_expires_at: string | null;
  video_deleted_at: string | null;
  /** Server-computed from video_deleted_at: 'available' | 'expired' */
  video_status: 'available' | 'expired';
  created_by_user_id: string | null;
  observation_types: string[] | null;
  /** UUID of the evaluation subject (the athlete). */
  athlete_id: string | null;
  athlete_display_name: string | null;
}

export interface EvaluationSearchResponse {
  items: EvaluationSearchItem[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

// ─── API call ─────────────────────────────────────────────────────────────────

/**
 * Search evaluations with optional filters, sort, and pagination.
 *
 * Permission rules are enforced server-side:
 * - Athletes can only see their own evaluations.
 * - Coaches see evaluations they triggered or in their channels.
 * - Admins see everything.
 *
 * GET /api/evaluations/search
 */
export async function searchEvaluations(
  params?: EvaluationSearchParams
): Promise<EvaluationSearchResponse> {
  const response = await apiClient.get<EvaluationSearchResponse>(
    '/evaluations/search',
    { params }
  );
  return response.data;
}
